/**
 * Cafe24 주문 상품 주문상태 매핑 및 헬퍼 유틸리티
 */

export const ORDER_STATUS_MAP: Record<string, string> = {
  // 1. 정상 주문 상태 (N)
  N00: "입금전",
  N01: "교환접수 - 교환상품",
  N02: "주문접수중",
  N03: "교환접수 - 카드결제대기",
  N10: "상품준비중",
  N20: "배송준비중",
  N21: "배송대기",
  N22: "배송보류",
  N30: "배송중",
  N40: "배송완료",
  N50: "구매확정",

  // 2. 취소 상태 (C)
  C00: "취소신청",
  C10: "취소접수 - 관리자",
  C11: "취소접수거부 - 관리자",
  C34: "취소처리중 - 환불전",
  C35: "취소처리중 - 환불완료",
  C36: "취소처리중 - 환불보류",
  C40: "취소완료",
  C41: "취소 완료 - 환불전",
  C42: "취소 완료 - 환불요청중",
  C43: "취소 완료 - 환불보류",
  C47: "입금전취소 - 구매자",
  C48: "입금전취소 - 자동취소",
  C49: "입금전취소 - 관리자",

  // 3. 반품 상태 (R)
  R00: "반품신청",
  R10: "반품접수",
  R11: "반품 접수 거부",
  R12: "반품보류",
  R13: "반품접수 - 수거완료(자동)",
  R20: "반품 수거 완료",
  R30: "반품처리중 - 수거전",
  R31: "반품처리중 - 수거완료",
  R34: "반품처리중 - 환불전",
  R36: "반품처리중 - 환불보류",
  R40: "반품완료 - 환불완료",
  R41: "반품완료 - 환불전",
  R42: "반품완료 - 환불요청중",
  R43: "반품완료 - 환불보류",

  // 4. 교환 상태 (E)
  E00: "교환신청",
  E10: "교환접수",
  E11: "교환접수거부",
  E12: "교환보류",
  E13: "교환접수 - 수거완료(자동)",
  E20: "교환준비",
  E30: "교환처리중 - 수거전",
  E31: "교환처리중 - 수거완료",
  E32: "교환처리중 - 입금전",
  E33: "교환처리중 - 입금완료",
  E34: "교환처리중 - 환불전",
  E35: "교환처리중 - 환불완료",
  E36: "교환처리중 - 환불보류",
  E40: "교환완료",
};

/**
 * 교환/카드결제 대기 상황에서의 N02 라벨
 */
export const ORDER_STATUS_N02_EXCHANGE = "입금전 - 카드결제대기";

/**
 * 주문 품목(Item) 또는 상태 코드로부터 상태 코드 문자열을 추출합니다.
 */
export function getOrderStatusCode(itemOrCode: any): string {
  if (itemOrCode === null || itemOrCode === undefined) return "";
  if (typeof itemOrCode === "string") {
    return itemOrCode.trim().toUpperCase();
  }
  if (typeof itemOrCode === "object") {
    const raw = String(
      itemOrCode.order_status ||
        itemOrCode.status_code ||
        itemOrCode.status ||
        ""
    ).trim();
    return raw.toUpperCase();
  }
  return "";
}

/**
 * 주문 품목(Item) 또는 상태 코드로부터 사람이 읽을 수 있는 한글 주문상태 명칭을 반환합니다.
 */
export function getOrderStatusText(itemOrCode: any): string {
  if (itemOrCode === null || itemOrCode === undefined) return "-";

  // 문자열 코드가 직접 전달된 경우
  if (typeof itemOrCode === "string") {
    const trimmed = itemOrCode.trim();
    if (!trimmed) return "-";
    // 이미 한글이 포함된 텍스트면 그대로 반환
    if (/[가-힣]/.test(trimmed)) return trimmed;
    const code = trimmed.toUpperCase();
    return ORDER_STATUS_MAP[code] || trimmed;
  }

  if (typeof itemOrCode !== "object") return "-";

  // 1. 이미 status_text에 한글 설명이 있는 경우 최우선 사용
  const statusText = itemOrCode.status_text;
  if (typeof statusText === "string" && statusText.trim()) {
    return statusText.trim();
  }

  // 2. order_status, status_code 등 코드 확인
  const rawCode = String(
    itemOrCode.order_status || itemOrCode.status_code || itemOrCode.status || ""
  ).trim();

  if (!rawCode) return "-";

  // rawCode 자체가 이미 한글 상태명인 경우 그대로 반환
  if (/[가-힣]/.test(rawCode)) {
    return rawCode;
  }

  const code = rawCode.toUpperCase();

  // N02의 경우 교환 컨텍스트(claim_code, is_exchange 등)에서는 '입금전 - 카드결제대기'로 표기
  if (code === "N02") {
    if (
      itemOrCode.claim_code ||
      itemOrCode.is_exchange ||
      itemOrCode.exchange_status
    ) {
      return ORDER_STATUS_N02_EXCHANGE;
    }
    return ORDER_STATUS_MAP.N02;
  }

  if (ORDER_STATUS_MAP[code]) {
    return ORDER_STATUS_MAP[code];
  }

  return rawCode;
}

/**
 * 상태 코드 또는 주문 품목에 맞는 Vuetify/Vercel 뱃지 CSS 클래스를 반환합니다.
 */
export function getOrderStatusBadgeClass(itemOrCode: any): string {
  const code = getOrderStatusCode(itemOrCode);
  if (!code) return "badge-secondary";

  // 취소 (C), 반품 (R): 빨간색
  if (code.startsWith("C") || code.startsWith("R")) {
    return "badge-red";
  }

  // 교환 (E), 입금대기/보류/지연 (N00, N01, N02, N03, N21, N22): 주황/경고색
  if (
    code.startsWith("E") ||
    code === "N00" ||
    code === "N01" ||
    code === "N02" ||
    code === "N03" ||
    code === "N21" ||
    code === "N22"
  ) {
    return "badge-warning";
  }

  // 배송중 (N30), 배송완료 (N40), 구매확정 (N50): 파란색
  if (code === "N30" || code === "N40" || code === "N50") {
    return "badge-blue";
  }

  // 기본 (상품준비중 N10, 배송준비중 N20 등): 중립 회색
  return "badge-secondary";
}
